import {AfterViewInit, Directive, OnDestroy} from '@angular/core';
import {DxDataGridComponent} from 'devextreme-angular';

/**
 * DevExtreme's default header-click cycle only toggles ascending/descending;
 * clearing sort otherwise requires the header's right-click "Clear Sorting" menu item.
 * This restores the standard 3-state left-click cycle (asc -> desc -> none) by
 * intercepting the desc -> asc wrap-around and clearing sort instead.
 *
 * Applies to every <dx-data-grid> automatically (element selector), so no template
 * changes are needed across the app's grids.
 */
@Directive({
  selector: 'dx-data-grid'
})
export class SortCycleTableDirective implements AfterViewInit, OnDestroy {
  // These grids all use single-column sorting, so only one column can be
  // genuinely "active" at a time; tracking a single pointer (rather than
  // per-column history) avoids acting on stale state left behind by a
  // column that lost its active sort when another column was clicked.
  private activeSort: { dataField: string; order: 'asc' | 'desc' } | null = null;
  private disposeOptionChanged?: () => void;

  constructor(private grid: DxDataGridComponent) {
  }

  ngAfterViewInit(): void {
    const instance = this.grid.instance;
    const handler = (e: any) => this.onOptionChanged(e);
    instance.on('optionChanged', handler);
    this.disposeOptionChanged = () => instance.off('optionChanged', handler);
  }

  ngOnDestroy(): void {
    this.disposeOptionChanged?.();
  }

  private onOptionChanged(e: any): void {
    const match = /^columns\[(\d+)\]\.sortOrder$/.exec(e.fullName);
    if (!match) {
      return;
    }

    const columnIndex = Number(match[1]);
    const column = e.component.columnOption(columnIndex);
    const dataField = column?.dataField ?? String(columnIndex);
    const newSortOrder = e.value as 'asc' | 'desc' | undefined;

    if (newSortOrder == null) {
      if (this.activeSort?.dataField === dataField) {
        this.activeSort = null;
      }
      return;
    }

    const activeSort = this.activeSort;
    if (activeSort !== null && activeSort.dataField === dataField && activeSort.order === 'desc' && newSortOrder === 'asc') {
      this.activeSort = null;
      e.component.columnOption(dataField, 'sortOrder', undefined);
      return;
    }

    this.activeSort = {dataField, order: newSortOrder};
  }
}
